import * as React from 'react';
import { ChangeEvent, RefObject, ReactNode, useContext, useEffect, createRef, useState } from 'react';
import compare from '@mms-v3/patch-lib';
import { UsersServiceApi } from '../../../api/admin/apis/UsersServiceApi';
import { UsersDTO } from '../../../api/admin/models/UsersDTO';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { defaultUserRole, pwPlaceholder, searchDelayDefault } from 'Settings/config';
import { userRoles } from 'Settings/data';
import { ApiResponse, type PagedModelUsersDTO, PatchDocument } from '../../../api/admin';
import { getAuthMiddleware } from '../../../Auth/utils/GetAuthMiddleware';
import { AuthContext } from '../../../Auth/components/AuthProvider';
import { DataTable, DataTablePageEvent, DataTableStateEvent } from 'primereact/datatable';
import { Column, ColumnEditorOptions } from 'primereact/column';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { Layout } from '../../Layout';
import { Direction } from '../../../util/types';

const defaultUser: UsersDTO = {
  username: '',
  givenname: '',
  surname: '',
  password: '',
  role: {role: defaultUserRole},
  type: 0,
};

const initialLazyTableState: DataTablePageEvent = {
  first: 0,
  rows: 10,
  page: 0,
  sortField: undefined,
  sortOrder: undefined,
  filters: {},
};

export function AdminAreaUser(): ReactNode {
  const intl: IntlShape = useIntl();
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [users, setUsers] = useState<UsersDTO[]>([]);
  const [lazyState, setLazyState] = useState<DataTablePageEvent>(initialLazyTableState);
  const [globalFilterValue, setGlobalFilterValue] = useState<string>('');
  const [add, setAdd] = useState<boolean>(false);
  const [addendum, setAddendum] = useState<UsersDTO>({...defaultUser});
  const toast: RefObject<Toast> = createRef<Toast>();

  const {actions} = useContext(AuthContext);
  const api: UsersServiceApi = new UsersServiceApi().withMiddleware(getAuthMiddleware(actions));

  useEffect((): void => {
    void lazyLoadData(globalFilterValue, lazyState);
  }, [lazyState]);

  useEffect((): () => void => {
    const delayDebounceFn = setTimeout(() => lazyLoadData(globalFilterValue, lazyState), searchDelayDefault);
    return (): void => clearTimeout(delayDebounceFn);
  }, [globalFilterValue]);

  const lazyLoadData = (searchParameter: string, dataTablePageEvent: DataTablePageEvent): void => {

    setLoading(true);
    void api
      .searchUser({
        page: dataTablePageEvent.page,
        size: dataTablePageEvent.rows,
        sort: dataTablePageEvent.sortField || undefined,
        direction: dataTablePageEvent.sortOrder > 0 ? Direction.ASC : Direction.DESC,
        searchParameter,
      })
      .then((pageUsers: PagedModelUsersDTO): void => {
        if (pageUsers) {
          setUsers(pageUsers.content ?? []);
          setTotalRecords(pageUsers.page.totalElements ?? pageUsers.content?.length ?? 0);
        }
        setLoading(false);
      })
      .catch((): void => {
        setLoading(false);
        toast.current.show({severity: 'error', summary: 'Error', detail: 'Loading users failed'});
      });
  };

  const onDelete = (userData: UsersDTO): void => {
    confirmDialog({
      message: 'Are you sure you want to delete this user?',
      header: 'Delete confirmation',
      icon: 'pi pi-exclamation-triangle',
      defaultFocus: 'reject',
      acceptClassName: 'p-button-danger',
      acceptLabel: 'Delete',
      rejectLabel: 'Abort',
      accept: (): void => deleteUser(userData),
      reject: (): void => {
      },
    });
  };

  const deleteUser = (userData: UsersDTO): void => {
    api
      .deleteUserRaw({id: userData.id})
      .then((): void => {
        void lazyLoadData(globalFilterValue, lazyState);
        toast.current.show({severity: 'success', summary: 'User deleted successfully'});
      })
      .catch((): void => {
        toast.current.show({severity: 'error', summary: 'Error', detail: 'Adding user failed'});
      });
  };

  const onPageChange = (e: DataTableStateEvent): void => setLazyState(e);

  const onSortChange = (e: DataTableStateEvent): void => setLazyState(e);

  const onFilterChange = (e: DataTableStateEvent): void => setLazyState({...e, first: 0});

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>): void => setGlobalFilterValue(e.target.value);

  const resetAddendum = (): void => {
    setAdd(false);
    setAddendum({...defaultUser});
  };

  const onRowAddInit = (): void => {
    setAdd(true);
  };

  const onRowAddComplete = (): void => {
    if (!isAddendumValid()) return;
    const usersDTO: UsersDTO = {
      username: addendum.username.trim(),
      givenname: addendum.givenname.trim(),
      surname: addendum.surname.trim(),
      password: addendum.password,
      role: addendum.role,
      type: addendum.type,
    };
    api.createUserRaw({usersDTO})
      .then((): void => {
        resetAddendum();
        void lazyLoadData(globalFilterValue, lazyState);
        toast.current.show({severity: 'success', summary: 'User added successfully'});
      })
      .catch((): void => {
        toast.current.show({severity: 'error', summary: 'Error', detail: 'Adding user failed'});
      });
  };

  const onRowAddCancel = (): void => {
    resetAddendum();
  };

  const isAddendumValid = (): boolean =>
    addendum.username.trim().length > 0 &&
    addendum.surname.trim().length > 0 &&
    addendum.givenname.trim().length > 0 &&
    addendum.password.length > 0;

  const onRowEditComplete = (e): void => {
    const patchDocument = compare(e.data, e.newData) as PatchDocument[];

    api.patchUserRaw({id: e.data.id, patchDocument})
      .then((response: ApiResponse<UsersDTO>): Promise<UsersDTO> => response.value())
      .then((userDTO: UsersDTO): void => {
        setUsers(users.map((user: UsersDTO): UsersDTO => user.id === userDTO.id ? userDTO : user));
        toast.current.show({severity: 'success', summary: 'User updated0 successfully'});
      })
      .catch((): void => {
        toast.current.show({severity: 'error', summary: 'Error', detail: 'Updating user failed'});
      });
  };

  const header = (
    <div className="flex justify-content-end">
      <IconField iconPosition="left">
        <InputIcon className="pi pi-search"/>
        <InputText value={globalFilterValue} onChange={onGlobalFilterChange}
                   placeholder={intl.formatMessage({id: 'search.placeholder'})}/>
      </IconField>
      <Button className={'ml-3'} icon={'pi pi-plus'} onClick={onRowAddInit} disabled={add}></Button>
    </div>
  );

  const roleEditor = (value: string, onChange: (e: DropdownChangeEvent) => any): ReactNode => {
    return (
      <Dropdown
        value={value}
        optionLabel={'role'}
        options={userRoles}
        onChange={onChange}
        placeholder="Select a role"
      />
    );
  };

  const headers = (
    <ColumnGroup>
      <Row>
        <Column colSpan={2} header={intl.formatMessage({id: 'userAdminArea.header.actions'})}
                headerStyle={{width: '10%', minWidth: '12rem'}}></Column>
        <Column header={intl.formatMessage({id: 'userAdminArea.header.username'})} sortable={true}
                field="username"></Column>
        <Column header={intl.formatMessage({id: 'userAdminArea.header.givenname'})} sortable={true}
                field="givenname"></Column>
        <Column header={intl.formatMessage({id: 'userAdminArea.header.surname'})} sortable={true}
                field="surname"></Column>
        <Column header={intl.formatMessage({id: 'userAdminArea.header.password'})} sortable={false}></Column>
        <Column header={intl.formatMessage({id: 'userAdminArea.header.role'})} sortable={false}></Column>
        <Column header={intl.formatMessage({id: 'userAdminArea.header.type'})} sortable={false}></Column>
      </Row>
      {add && <Row>
        <Column
          headerStyle={{textAlign: 'center', minWidth: '8rem'}} header={
          <div className={'flex'} style={{textAlign: 'center'}}>
            <Button icon="pi pi-check" rounded text severity="secondary" aria-label="Persist" onClick={onRowAddComplete}
                    disabled={!isAddendumValid()}/>
            <Button icon="pi pi-times" rounded text severity="secondary" aria-label="Cancel" onClick={onRowAddCancel}/>
          </div>
        }></Column>
        <Column headerStyle={{textAlign: 'center', minWidth: '4rem'}}></Column>
        <Column header={
          <InputText value={addendum.username} onChange={(e: ChangeEvent<HTMLInputElement>): void => setAddendum({
            ...addendum,
            username: e.target.value,
          })}/>
        }></Column>
        <Column header={
          <InputText value={addendum.givenname} onChange={(e: ChangeEvent<HTMLInputElement>): void => setAddendum({
            ...addendum,
            givenname: e.target.value,
          })}/>
        }></Column>
        <Column header={
          <InputText value={addendum.surname} onChange={(e: ChangeEvent<HTMLInputElement>): void => setAddendum({
            ...addendum,
            surname: e.target.value,
          })}/>
        }></Column>
        <Column header={
          <Password value={addendum.password} onChange={(e: ChangeEvent<HTMLInputElement>): void => setAddendum({
            ...addendum,
            password: e.target.value,
          })} toggleMask={true}/>
        }></Column>
        <Column header={
          roleEditor(
            addendum.role.role,
            (e: DropdownChangeEvent) => setAddendum({...addendum, role: {role: e.target.value}}),
          )
        }></Column>
        <Column header={'Type'}></Column>
      </Row>}
    </ColumnGroup>
  );

  return (
    <Layout title={intl.formatMessage({id: 'userAdminArea.title'})}>
      <Toast ref={toast}/>
      <ConfirmDialog/>
      <DataTable
        headerColumnGroup={headers}
        value={users}
        lazy={true}
        stripedRows={true}
        showGridlines={true}
        dataKey="id"
        paginator={true}
        first={lazyState.first}
        totalRecords={totalRecords}
        sortField={lazyState.sortField}
        sortOrder={lazyState.sortOrder}
        filters={lazyState.filters}
        loading={loading}
        rowsPerPageOptions={[10, 25, 50]}
        rows={lazyState.rows}
        header={header}
        editMode="row"
        onPage={onPageChange}
        onSort={onSortChange}
        onFilter={onFilterChange}
        onRowEditComplete={onRowEditComplete}
      >
        <Column rowEditor={true} bodyStyle={{textAlign: 'center', minWidth: '8rem'}}></Column>
        <Column bodyStyle={{textAlign: 'center', minWidth: '4rem'}}
                body={(row: UsersDTO): ReactNode => <Button icon={'pi pi-trash'} rounded text severity="secondary"
                                                            aria-label="Delete"
                                                            onClick={() => onDelete(row)}></Button>}
        ></Column>
        <Column field="username"></Column>
        <Column field="givenname"></Column>
        <Column field="surname"></Column>
        <Column field="password" body={pwPlaceholder}></Column>
        <Column field="role"
                editor={(options: ColumnEditorOptions): ReactNode => roleEditor(
                  options.value?.role,
                  (e: DropdownChangeEvent): void => options.editorCallback({id: options.rowData.id, role: e.value}),
                )}
                body={(user: UsersDTO): string => user.role?.role}
        ></Column>
        <Column field="type"
                body={
                  (user: UsersDTO): ReactNode => user.type === 1
                    ? (<FormattedMessage id="userAdminArea.body.type.extern"/>)
                    : (<FormattedMessage id="userAdminArea.body.type.intern"/>)
                }
        ></Column>
      </DataTable>
    </Layout>
  );
}

export default AdminAreaUser;
